/**
 * Week view — 7-column time grid with track-packed event blocks.
 *
 * @module components/calendar/WeekView
 */

import { useMemo } from 'react'
import { format, startOfWeek, addDays } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { meetingVisibleOnDay, packTracks } from '../../utils/calendarLayout'
import TimeGrid from './TimeGrid'
import EventBlock from './EventBlock'

const HOUR_HEIGHT = 60

export default function WeekView({ meetings, currentDate, userTz }) {
  const weekDays = useMemo(() => {
    const dateInTz = utcToZonedTime(currentDate, userTz)
    const ws = startOfWeek(dateInTz)
    return Array.from({ length: 7 }, (_, i) => addDays(ws, i))
  }, [currentDate, userTz])

  const columnHeaders = useMemo(
    () =>
      weekDays.map((d) => ({
        label: format(d, 'EEE'),
        sublabel: format(d, 'd'),
        date: d,
      })),
    [weekDays]
  )

  // Pre-compute packed events for each column
  const packedByColumn = useMemo(() => {
    return weekDays.map((day) => {
      const dayMeetings = meetings.filter((m) =>
        meetingVisibleOnDay(m, day, userTz)
      )
      return packTracks(dayMeetings, userTz, HOUR_HEIGHT)
    })
  }, [meetings, weekDays, userTz])

  const totalMeetings = packedByColumn.reduce((sum, col) => sum + col.length, 0)

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <TimeGrid
        columnCount={7}
        columnHeaders={columnHeaders}
        hourHeight={HOUR_HEIGHT}
        userTz={userTz}
        currentDate={currentDate}
        renderColumn={(colIdx) =>
          packedByColumn[colIdx].map((item) => (
            <EventBlock
              key={item.meeting.id ?? item.meeting.meetingId ?? `${colIdx}-${item.top}`}
              meeting={item.meeting}
              top={item.top}
              height={item.height}
              track={item.track}
              totalTracks={item.totalTracks}
              userTz={userTz}
            />
          ))
        }
      />
      {totalMeetings === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">
          No meetings scheduled this week
        </div>
      )}
    </div>
  )
}
